import type { PathCommand } from 'opentype.js';
import { rectFromPoints, type Mat2D, type Rect } from '../math';

/**
 * A closed polygon approximating one glyph contour, in canvas pixel space.
 * Points are stored flat as [x0, y0, x1, y1, ...] — struct-of-arrays, so techniques
 * can walk them without allocating a point object per vertex.
 */
export interface Contour {
  points: Float32Array;
  /** Signed area; sign encodes winding (used to tell outer contours from counters). */
  signedArea: number;
  /** Total perimeter length in px, precomputed for arc-length sampling. */
  length: number;
  bounds: Rect;
}

/**
 * Adaptive Bézier flattening by recursive subdivision.
 *
 * Flatness criterion after Hain et al.: subdivide while the control points deviate
 * from the chord by more than `tolerance`. This gives dense vertices in tight curves
 * and sparse ones on near-straight segments, which matters because several techniques
 * (hatching, outline sampling, DLA seeding) walk these polygons per frame.
 */
const MAX_DEPTH = 16;

function flattenCubic(
  out: number[],
  x0: number, y0: number,
  x1: number, y1: number,
  x2: number, y2: number,
  x3: number, y3: number,
  tol2: number,
  depth: number,
): void {
  if (depth >= MAX_DEPTH) {
    out.push(x3, y3);
    return;
  }
  // Distance of the two control points from the chord p0→p3.
  const dx = x3 - x0;
  const dy = y3 - y0;
  const d1 = Math.abs((x1 - x3) * dy - (y1 - y3) * dx);
  const d2 = Math.abs((x2 - x3) * dy - (y2 - y3) * dx);
  const dd = d1 + d2;
  if (dd * dd <= tol2 * (dx * dx + dy * dy)) {
    out.push(x3, y3);
    return;
  }
  const x01 = (x0 + x1) / 2, y01 = (y0 + y1) / 2;
  const x12 = (x1 + x2) / 2, y12 = (y1 + y2) / 2;
  const x23 = (x2 + x3) / 2, y23 = (y2 + y3) / 2;
  const x012 = (x01 + x12) / 2, y012 = (y01 + y12) / 2;
  const x123 = (x12 + x23) / 2, y123 = (y12 + y23) / 2;
  const xm = (x012 + x123) / 2, ym = (y012 + y123) / 2;
  flattenCubic(out, x0, y0, x01, y01, x012, y012, xm, ym, tol2, depth + 1);
  flattenCubic(out, xm, ym, x123, y123, x23, y23, x3, y3, tol2, depth + 1);
}

function flattenQuadratic(
  out: number[],
  x0: number, y0: number,
  x1: number, y1: number,
  x2: number, y2: number,
  tol2: number,
  depth: number,
): void {
  if (depth >= MAX_DEPTH) {
    out.push(x2, y2);
    return;
  }
  const dx = x2 - x0;
  const dy = y2 - y0;
  const d = Math.abs((x1 - x2) * dy - (y1 - y2) * dx);
  if (d * d <= tol2 * (dx * dx + dy * dy)) {
    out.push(x2, y2);
    return;
  }
  const x01 = (x0 + x1) / 2, y01 = (y0 + y1) / 2;
  const x12 = (x1 + x2) / 2, y12 = (y1 + y2) / 2;
  const xm = (x01 + x12) / 2, ym = (y01 + y12) / 2;
  flattenQuadratic(out, x0, y0, x01, y01, xm, ym, tol2, depth + 1);
  flattenQuadratic(out, xm, ym, x12, y12, x2, y2, tol2, depth + 1);
}

function finishContour(raw: number[], transform: Mat2D | null): Contour | null {
  if (raw.length < 6) return null; // fewer than 3 vertices — not a polygon

  let pts = new Float32Array(raw.length);
  if (transform) {
    const { a, b, c, d, e, f } = transform;
    for (let i = 0; i < raw.length; i += 2) {
      const x = raw[i];
      const y = raw[i + 1];
      pts[i] = a * x + c * y + e;
      pts[i + 1] = b * x + d * y + f;
    }
  } else {
    pts.set(raw);
  }

  // Drop a duplicated closing vertex if the path repeated the start point.
  const n = pts.length;
  if (Math.abs(pts[0] - pts[n - 2]) < 1e-6 && Math.abs(pts[1] - pts[n - 1]) < 1e-6) {
    pts = pts.subarray(0, n - 2);
  }
  if (pts.length < 6) return null;

  let area = 0;
  let length = 0;
  for (let i = 0; i < pts.length; i += 2) {
    const j = (i + 2) % pts.length;
    area += pts[i] * pts[j + 1] - pts[j] * pts[i + 1];
    length += Math.hypot(pts[j] - pts[i], pts[j + 1] - pts[i + 1]);
  }

  return {
    points: pts,
    signedArea: area / 2,
    length,
    bounds: rectFromPoints(pts),
  };
}

/**
 * Flattens opentype path commands into closed polygons.
 *
 * `tolerance` is expressed in the *output* space: pass the transform that will be
 * applied afterwards and the tolerance is pre-divided by its scale, so a glyph that
 * gets scaled up 10× is still flattened to sub-pixel accuracy on screen.
 */
export function flattenCommands(
  commands: readonly PathCommand[],
  tolerance: number,
  transform: Mat2D | null = null,
): Contour[] {
  const contours: Contour[] = [];
  const tol = Math.max(1e-3, tolerance);
  const tol2 = tol * tol;

  let current: number[] = [];
  let cx = 0;
  let cy = 0;
  let startX = 0;
  let startY = 0;

  const close = (): void => {
    const contour = finishContour(current, transform);
    if (contour) contours.push(contour);
    current = [];
  };

  for (const cmd of commands) {
    switch (cmd.type) {
      case 'M':
        if (current.length) close();
        cx = startX = cmd.x;
        cy = startY = cmd.y;
        current.push(cx, cy);
        break;
      case 'L':
        current.push(cmd.x, cmd.y);
        cx = cmd.x;
        cy = cmd.y;
        break;
      case 'Q':
        flattenQuadratic(current, cx, cy, cmd.x1, cmd.y1, cmd.x, cmd.y, tol2, 0);
        cx = cmd.x;
        cy = cmd.y;
        break;
      case 'C':
        flattenCubic(current, cx, cy, cmd.x1, cmd.y1, cmd.x2, cmd.y2, cmd.x, cmd.y, tol2, 0);
        cx = cmd.x;
        cy = cmd.y;
        break;
      case 'Z':
        close();
        cx = startX;
        cy = startY;
        break;
    }
  }
  if (current.length) close();

  return contours;
}

/** Builds a Path2D for rasterisation. Nonzero winding keeps glyph counters hollow. */
export function contoursToPath2D(contours: readonly Contour[]): Path2D {
  const path = new Path2D();
  for (const c of contours) {
    const p = c.points;
    if (p.length < 6) continue;
    path.moveTo(p[0], p[1]);
    for (let i = 2; i < p.length; i += 2) path.lineTo(p[i], p[i + 1]);
    path.closePath();
  }
  return path;
}

/** SVG path data for the given contours — used by the vector exporters. */
export function contoursToPathData(contours: readonly Contour[], decimals = 2): string {
  const f = (v: number): string => {
    const r = v.toFixed(decimals);
    return r.replace(/\.?0+$/, '') || '0';
  };
  let out = '';
  for (const c of contours) {
    const p = c.points;
    if (p.length < 6) continue;
    out += `M${f(p[0])} ${f(p[1])}`;
    for (let i = 2; i < p.length; i += 2) out += `L${f(p[i])} ${f(p[i + 1])}`;
    out += 'Z';
  }
  return out;
}
